import { useState, useCallback } from "react";

export interface PortFormData {
  // Basic port fields
  portId: string;
  portName: string;
  portNumber: string;
  portType: string;
  portStatus: string;
  l2Description: string;
  l3Description: string;
  
  // Additional fields
  editHardwareId: string;
  editCapacityMbps: string;
  editProvider: string;
  editProviderAccount: string; // "1" for Telecom, "2" for CTX
  editProviderId: string;
  editLagId: string;
  editCreatedAt: string;
  editTenantName: string;
  editChargebeeId: string;
  editLineId: string;
  editOrderId: string;
  editCorrelation: string;
  editDcId: string;
  editDcName: string;
  editDcDisplayName: string;
  editDcCity: string;
  
  // Status toggle (0 = Draft, 1 = Active)
  isDraft: number;
  
  // P2P Addressing
  mask: string;
  peerIp: string;
  gateway: string;
  network: string;
  
  // Tags and Terms
  tags: string[];
  terms: string;

  // Subscription ID
  subscriptionId: string;

  // Crossconnect Information (for virtual ports)
  crossconnectType: string; // SMF, UTP, GLC-T
  crossconnectSpeed: string; // 100Mb, 1G, 100G
  crossconnectId: string;
  
  // Tenant and Port Type
  tenantId: number | null;
  isVirtualPort: boolean; // false = Physical, true = Virtual
  
  // Internal IDs
  id: number | null;
  hardwareResourceId: number | null;
}

const initialFormData: PortFormData = {
  portId: "",
  portName: "",
  portNumber: "",
  portType: "",
  portStatus: "",
  l2Description: "",
  l3Description: "",
  editHardwareId: "",
  editCapacityMbps: "",
  editProvider: "",
  editProviderAccount: "",
  editProviderId: "",
  editLagId: "",
  editCreatedAt: "",
  editTenantName: "",
  editChargebeeId: "",
  editLineId: "",
  editOrderId: "",
  editCorrelation: "",
  editDcId: "",
  editDcName: "",
  editDcDisplayName: "",
  editDcCity: "",
  isDraft: 0,
  mask: "",
  peerIp: "",
  gateway: "",
  network: "",
  tags: [],
  terms: "",
  subscriptionId: "",
  crossconnectType: "",
  crossconnectSpeed: "",
  crossconnectId: "",
  tenantId: null,
  isVirtualPort: false, // Default to Physical
  id: null,
  hardwareResourceId: null,
};

export interface UsePortFormReturn {
  formData: PortFormData;
  updateField: <K extends keyof PortFormData>(field: K, value: PortFormData[K]) => void;
  updateFields: (fields: Partial<PortFormData>) => void;
  resetForm: () => void;
  setFormData: (data: Partial<PortFormData>) => void;
}

export const usePortForm = (initialData?: Partial<PortFormData>): UsePortFormReturn => {
  const [formData, setFormDataState] = useState<PortFormData>({
    ...initialFormData,
    ...initialData,
  });

  const updateField = useCallback(<K extends keyof PortFormData>(
    field: K,
    value: PortFormData[K]
  ) => {
    setFormDataState((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const updateFields = useCallback((fields: Partial<PortFormData>) => {
    setFormDataState((prev) => ({
      ...prev,
      ...fields,
    }));
  }, []);

  const resetForm = useCallback(() => {
    setFormDataState({
      ...initialFormData,
      ...initialData,
    });
  }, [initialData]);

  const setFormData = useCallback((data: Partial<PortFormData>) => {
    setFormDataState((prev) => ({
      ...prev,
      ...data,
    }));
  }, []);

  return {
    formData,
    updateField,
    updateFields,
    resetForm,
    setFormData,
  };
};

